import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import Breadcrumb from "@components/UI/Breadcrumb";
import CustomSelect from "@components/UI/CustomSelect";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { CustomDatePicker } from "@components/Forms";
import ToggleSwitch from "@components/Forms/ToggleSwitch";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { ConfirmActionModal } from "@components/UI";
import { translateDepartment } from "@utils/common";
import { PermissionContext } from "@contexts/PermissionContext";
import { useContext } from "react";

const AddProduct = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [loading, setLoading] = useState(false);
  const [showDefaultProductConfirmModal, setShowDefaultProductConfirmModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { productSeq } = useParams();
  const isEdit = !!productSeq;
  const { hasManagePermission } = useContext(PermissionContext);

  const [formData, setFormData] = useState({
    productName: "",
    productManager: "",
    appliedModel: "",
    usageScope: "",
    structureEngineering: "",
    inspectionDate: "",
    releaseDate: "",
    developmentDepartment: "",
    mainContact: "",
    defaultFlag: "N",
  });

  const { creatorList, departmentList, fetchSearchInfo, defaultProduct } = useSearchInfo();
  const isFirstRender = useRef(true);

  // Filter users with PRODUCT_MANAGER permission
  const productManagerList = creatorList.filter(user =>
    user.permissions && user.permissions.includes("PRODUCT_MANAGER") && user.enableFlag === "Y"
  );

  const structureEngineeringList = creatorList.filter(user =>
    user.permissions && user.permissions.includes("DEVELOPER") && user.userId !== "system" && user.enableFlag === "Y"
  );

  const developmentDepartmentList = (departmentList || []).map(item => ({
    value: item.value,
    label: translateDepartment(t, item.value),
  }));

  const breadcrumbItems = [
    { label: t("product.product-management"), href: "/products" },
    { label: location.search ? t("product.clone-product") : t("product.add-product"), href: "/products/new" },
  ];

  // Fetch product data when editing
  const getProductDetail = useMutationData(
    queryKeys.getProductDetail,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response) {
          setFormData({
            productName: response.productName || "",
            productManager: response.productManager || "",
            remark: response.remark || "",
            defaultFlag: response.defaultFlag || "N",
          });
        }
      },
      onError: (error) => {
        console.error("Error fetching product detail:", error);
        showErrorToast(t("system.fetch-product-error"));
      },
    },
  );

  // Function to parse URL params and populate form data
  const parseUrlParams = () => {
    const searchParams = new URLSearchParams(location.search);
    const cloneData = {};

    // Get all URL parameters
    for (const [key, value] of searchParams.entries()) {
      cloneData[key] = value;
    }

    // Convert date strings back to Date objects
    if (cloneData.inspectionDate) {
      cloneData.inspectionDate = new Date(cloneData.inspectionDate);
    }
    if (cloneData.releaseDate) {
      cloneData.releaseDate = new Date(cloneData.releaseDate);
    }

    return cloneData;
  };

  // Fetch search info on component mount
  useEffect(() => {
    if (isFirstRender.current) {
      fetchSearchInfo();
      isFirstRender.current = false;
    }
  }, [fetchSearchInfo]);

  // Auto fill form data from URL params (for clone functionality)
  useEffect(() => {
    if (!isEdit && location.search) {
      const cloneData = parseUrlParams();
      if (Object.keys(cloneData).length > 0) {
        setFormData(prev => ({
          ...prev,
          ...cloneData
        }));
      }
    }
  }, [location.search, isEdit]);

  // Load product data when editing
  useEffect(() => {
    if (isEdit && productSeq) {
      getProductDetail.mutate({ productSeq });
    }
  }, [isEdit, productSeq]);

  const createProduct = useMutationData(
    queryKeys.createProduct,
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        showSuccessToast(t("product.product-added-successfully"));
        setLoading(false);
        navigate("/products/" + response.productSeq);
      },
      onError: (error) => {
        console.error("Error adding product:", error);
        
        // Check for duplicate product name error
        const errorMessage = error?.response?.data;
        if (error?.response?.status === 400 && 
            errorMessage && 
            (errorMessage.includes("Duplicated product name") || errorMessage.includes("duplicated"))) {
          showErrorToast(t("product.duplicate-product-name-error"), "");
        } else {
          showErrorToast(t("system.add-product-failed"), "");
        }
        
        setLoading(false);
      },
    },
  );

  const updateProduct = useMutationData(
    queryKeys.updateProduct,
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        showSuccessToast(t("system.update-product-successfully"));
        setLoading(false);
        navigate("/products/" + response.productSeq);
      },
      onError: (error) => {
        console.error("Error updating product:", error);
        
        // Check for duplicate product name error
        const errorMessage = error?.response?.data;
        if (error?.response?.status === 400 && 
            errorMessage && 
            (errorMessage.includes("Duplicated product name") || errorMessage.includes("duplicated"))) {
          showErrorToast(t("product.duplicate-product-name-error"), "");
        } else {
          showErrorToast(t("system.update-product-error") + ": " + errorMessage, "");
        }
        
        setLoading(false);
      },
    },
  );

  // Handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    // Check if we came from cloning a product
    const searchParams = new URLSearchParams(location.search);
    const clonedFrom = searchParams.get('clonedFrom');

    if (clonedFrom) {
      // Navigate back to the product detail page
      navigate(`/products/${clonedFrom}`);
    } else {
      // Navigate to products list
      navigate("/products");
    }
  };

  const validateForm = () => {
    return formData.productName.trim() !== "" && formData.productManager !== null && formData.productManager !== "";
  };

  const isFormValid = () => {
    return validateForm();
  };

  const shouldConfirmDefaultProductChange = () => {
    if (!hasManagePermission) return false;
    if ((formData.defaultFlag || "N") !== "Y") return false;
    if (defaultProduct == null) return false;
    if (isEdit && Number(productSeq) === defaultProduct.productSeq) return false;
    return true;
  };

  const submitProduct = () => {
    setLoading(true);

    const payload = {
      productName: formData.productName,
      productManager: formData.productManager,
      appliedModel: formData.appliedModel,
      usageScope: formData.usageScope,
      structuralEngineer: formData.structureEngineering,
      inspectionDate: formData.inspectionDate,
      releaseDate: formData.releaseDate,
      developmentDepartment: formData.developmentDepartment,
      mainContact: formData.mainContact,
    };

    if (hasManagePermission) {
      payload.defaultFlag = formData.defaultFlag || "N";
    }

    if (isEdit) {
      updateProduct.mutate(payload);
    } else {
      createProduct.mutate(payload);
    }
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    if (shouldConfirmDefaultProductChange()) {
      setShowDefaultProductConfirmModal(true);
      return;
    }

    submitProduct();
  };

  const handleConfirmDefaultProductChange = () => {
    if (defaultProduct == null) return;
    setShowDefaultProductConfirmModal(false);
    submitProduct();
  };

  const handleCancelDefaultProductChange = () => {
    setShowDefaultProductConfirmModal(false);
  };

  return (
    <div data-testid="add-product-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`}>
        <div className="flex items-center justify-between">
          <div className="items-center space-x-4">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className={`${colors.textPrimary} text-2xl font-bold uppercase`}>
              {location.search ? t("product.clone-product") : t("product.add-product")}
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <OutlineButton onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
            <PrimaryButton data-testid="add-product-save-button" onClick={handleSave} disabled={!isFormValid() || loading}>
              {t("common.save-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="add-product-form">
        <div className={`mx-auto max-w-[55rem]`}>
          <div>
            {/* Product Information Section */}
            <div className="mb-6">
              <h2 className={`${colors.textPrimary} text-xl font-semibold`}>
                {t("product.product-information")}
              </h2>
              <p className={`mb-2 ${colors.textSubTitle} text-sm font-normal`}>
                {t("project.required-fields-note")} <span className={`${colors.textRequired}`}>*</span>
              </p>

              <div className="grid grid-cols-3 gap-6">
                {/* Product Name */}
                <div className="col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.product-name")} <span className={`${colors.textRequired}`}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t("product.product-name-placeholder")}
                    value={formData.productName}
                    onChange={(e) => handleInputChange("productName", e.target.value)}
                    title={formData.productName}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    maxLength={1024}
                  />
                </div>

                {/* Product Manager */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.product-manager")} <span className={`${colors.textRequired}`}>*</span>
                  </label>
                  <CustomSelect
                    options={productManagerList}
                    value={productManagerList.find((opt) => opt.value === formData.productManager)}
                    onChange={(opt) => handleInputChange("productManager", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={true}
                    className="w-full text-sm font-medium"
                    displayFormatLabelWithID={true}
                  />
                </div>

                {/* Applied Model */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.applied-model")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("product.applied-model-placeholder")}
                    value={formData.appliedModel}
                    onChange={(e) => handleInputChange("appliedModel", e.target.value)}
                    title={formData.appliedModel}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    maxLength={255}
                  />
                </div>

                {/* Usage Scope */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.usage-scope")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("product.usage-scope-placeholder")}
                    value={formData.usageScope}
                    onChange={(e) => handleInputChange("usageScope", e.target.value)}
                    title={formData.usageScope}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    maxLength={255}
                  />
                </div>

                {/* Structure Engineering */}
                {/* <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.structure-engineering")}
                  </label>
                  <CustomSelect
                    options={structureEngineeringList}
                    value={structureEngineeringList.find((opt) => opt.value === formData.structureEngineering)}
                    onChange={(opt) => handleInputChange("structureEngineering", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={true}
                    className="w-full text-sm font-medium"
                    displayFormatLabelWithID={true}
                  />
                </div> */}

                {/* Inspection Date */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.inspection-date")}
                  </label>
                  <CustomDatePicker
                    selected={formData.inspectionDate}
                    onChange={(date) => handleInputChange("inspectionDate", date)}
                    placeholderText={t("project.select-date-placeholder")}
                    title={formData.inspectionDate ? formData.inspectionDate.toLocaleDateString() : ""}
                    className={`h-10 w-full appearance-none rounded-lg border p-3 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date()}
                  />
                </div>

                {/* Release Date */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.release-date")}
                  </label>
                  <CustomDatePicker
                    selected={formData.releaseDate}
                    onChange={(date) => handleInputChange("releaseDate", date)}
                    placeholderText={t("project.select-date-placeholder")}
                    title={formData.releaseDate ? formData.releaseDate.toLocaleDateString() : ""}
                    className={`h-10 w-full appearance-none rounded-lg border p-3 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date()}
                  />
                </div>

                {/* Dev. Department */}
                {/* <div className="relative">
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                      {t("product.development-department")}
                    </label>

                    <CustomSelect
                      options={developmentDepartmentList}
                      value={developmentDepartmentList.find((opt) => opt.value === formData.developmentDepartment)}
                      onChange={(opt) => handleInputChange("developmentDepartment", opt?.value || null)}
                      placeholder={t("common.please-select")}
                      isSearchable={true}
                      className="w-full text-sm font-medium"
                    />
                  </div> */}

                {/* Main Contact */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.main-contact")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("product.main-contact-placeholder")}
                    value={formData.mainContact}
                    onChange={(e) => handleInputChange("mainContact", e.target.value)}
                    title={formData.mainContact}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    maxLength={255}
                  />
                </div>

                {/* Default Product */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("product.default-product", "Default Product")}
                  </label>
                  <div className="flex h-10 items-center space-x-2">
                    <ToggleSwitch
                      checked={(formData.defaultFlag || "N") === "Y"}
                      onChange={(checked) => handleInputChange("defaultFlag", checked ? "Y" : "N")}
                      disabled={!hasManagePermission}
                    />
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmActionModal
        isOpen={showDefaultProductConfirmModal && defaultProduct != null}
        onClose={handleCancelDefaultProductChange}
        onConfirm={handleConfirmDefaultProductChange}
        title={t("product.change-default-product-confirmation-title")}
        description={t("product.change-default-product-confirmation-description", {
          productName: defaultProduct?.productName,
        })}
        confirmText={t("common.yes")}
        loading={loading}
        data-testid="change-default-product-confirmation-modal"
      />
    </div>
  );
};

export default AddProduct;
